import { useContext, useState } from 'react';

import BpkSelectableChip from '@skyscanner-internal/backpack-web/bpk-component-chip';
import {
  BpkBox,
  BpkBreakpoint,
  BpkFlex,
  BpkSpacing,
} from '@skyscanner-internal/backpack-web/bpk-component-layout';
import BpkMobileScrollContainer from '@skyscanner-internal/backpack-web/bpk-component-mobile-scroll-container';
import reactReplacePlaceholder from 'react-replace-placeholder';

import StringsContext from '../Contexts/StringsContext';
import { BOARD_TYPE } from '../server/constants';

import type { Board } from '../ArrivalsDeparturesBoardReducer';

export interface FlightChipsProps {
  airportName: string;
  currentBoard: string;
  onChipClick: (selectedChip: Board) => void;
}

const FlightChipGroup = ({
  airportName,
  currentBoard,
  onChipClick,
}: FlightChipsProps) => {
  const strings = useContext(StringsContext);
  const [scrollerRef, setScrollerRef] = useState<HTMLElement | null>(null);

  const arrivalsChipLabel = reactReplacePlaceholder(
    strings.falcon_adb_arrivals_chip,
    { airport_name: airportName },
    '@@',
    '@@',
  ).join('');
  const departuresChipLabel = reactReplacePlaceholder(
    strings.falcon_adb_departures_chip,
    { airport_name: airportName },
    '@@',
    '@@',
  ).join('');

  const handleChipClick = (board: keyof typeof BOARD_TYPE) => {
    scrollerRef?.scrollTo({
      left: board === BOARD_TYPE.ARRIVALS ? 0 : scrollerRef?.scrollWidth,
      behavior: 'smooth',
    });

    onChipClick(board);
  };

  return (
    // max-width 1224px has no matching BpkSpacing token; converted to an
    // explicit rem value (1224px = 76.5rem) — BpkSizeValue (unlike
    // BpkMarginValue) accepts arbitrary rem. paddingBottom is Base in both
    // the mobile and tablet+ legacy rules, so it needs no responsive object.
    <BpkBox
      maxWidth="76.5rem"
      marginInline="auto"
      paddingTop={{
        base: BpkSpacing.Base,
        [BpkBreakpoint.SmallTablet]: BpkSpacing.XL,
      }}
      paddingBottom={BpkSpacing.Base}
      data-cy="flight-chip-group"
    >
      <BpkMobileScrollContainer scrollerRef={(el) => setScrollerRef(el)}>
        <BpkFlex
          gap={BpkSpacing.MD}
          paddingInline={{
            base: BpkSpacing.Base,
            [BpkBreakpoint.SmallTablet]: BpkSpacing.None,
          }}
        >
          <BpkSelectableChip
            accessibilityLabel={arrivalsChipLabel}
            name={BOARD_TYPE.ARRIVALS}
            selected={currentBoard === BOARD_TYPE.ARRIVALS}
            onClick={() => handleChipClick(BOARD_TYPE.ARRIVALS)}
          >
            {arrivalsChipLabel}
          </BpkSelectableChip>
          <BpkSelectableChip
            accessibilityLabel={departuresChipLabel}
            name={BOARD_TYPE.DEPARTURES}
            selected={currentBoard === BOARD_TYPE.DEPARTURES}
            onClick={() => handleChipClick(BOARD_TYPE.DEPARTURES)}
          >
            {departuresChipLabel}
          </BpkSelectableChip>
        </BpkFlex>
      </BpkMobileScrollContainer>
    </BpkBox>
  );
};

export default FlightChipGroup;
